import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer
} from 'recharts';

const SERIES = [
  { key: 'entra',    label: 'Entra ID',  color: '#3b82f6' },
  { key: 'intune',   label: 'Intune',    color: '#06b6d4' },
  { key: 'graph',    label: 'Graph API', color: '#a3e635' },
  { key: 'm365',     label: 'M365',      color: '#f59e0b' },
  { key: 'defender', label: 'Defender',  color: '#34d399' },
];

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload || !payload.length) return null;
  return (
    <div className="chart-tooltip">
      <p className="chart-tooltip-label">{label}</p>
      {payload
        .filter(p => p.value > 0)
        .sort((a, b) => b.value - a.value)
        .map(p => (
          <div key={p.dataKey} className="chart-tooltip-row">
            <span className="chart-tooltip-dot" style={{ background: p.stroke }} />
            <span className="chart-tooltip-name">{p.name}</span>
            <span className="chart-tooltip-value">{p.value}</span>
          </div>
        ))
      }
    </div>
  );
};

const CommitActivityChart = () => {
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [hidden, setHidden] = useState({});

  const toggleSeries = (key) => {
    setHidden(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const fetchAllData = async () => {
      try {
        const [entraRes, intuneRes, graphRes, m365Res, defenderRes] = await Promise.all([
          fetch('/entra-changes.json'),
          fetch('/intune-changes.json'),
          fetch('/graph-changes.json'),
          fetch('/m365-changes.json'),
          fetch('/defender-changes.json')
        ]);

        const [entraData, intuneData, graphData, m365Data, defenderData] = await Promise.all([
          entraRes.json(),
          intuneRes.json(),
          graphRes.json(),
          m365Res.json(),
          defenderRes.json()
        ]);

        const processCommitsByDay = (data) => {
          const commitsByDay = {};
          const sevenDaysAgo = new Date();
          sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
          const changes = Array.isArray(data.changes)
            ? data.changes
            : data.changes
              ? [data.changes]
              : [];
          changes.forEach(commit => {
            const commitDate = new Date(commit.date);
            if (commitDate >= sevenDaysAgo) {
              const dateKey = commitDate.toISOString().split('T')[0];
              commitsByDay[dateKey] = (commitsByDay[dateKey] || 0) + 1;
            }
          });
          return commitsByDay;
        };

        const allCommits = {
          entra:    processCommitsByDay(entraData),
          intune:   processCommitsByDay(intuneData),
          graph:    processCommitsByDay(graphData),
          m365:     processCommitsByDay(m365Data),
          defender: processCommitsByDay(defenderData),
        };

        const last7Days = [];
        for (let i = 6; i >= 0; i--) {
          const date = new Date();
          date.setDate(date.getDate() - i);
          last7Days.push(date.toISOString().split('T')[0]);
        }

        const combined = last7Days.map(date => {
          const dateObj = new Date(date);
          const formattedDate = dateObj.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
          const point = { date: formattedDate };
          SERIES.forEach(({ key }) => { point[key] = allCommits[key][date] || 0; });
          return point;
        });

        setChartData(combined);
        setLoading(false);
      } catch (error) {
        console.error('Error fetching commit data:', error);
        setLoading(false);
      }
    };
    fetchAllData();
  }, []);

  if (loading) {
    return (
      <div className="charts-modern-card">
        <div className="chart-loading">Loading commit activity...</div>
      </div>
    );
  }

  return (
    <div className="charts-modern-card">
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: -15, bottom: 0 }}>
          <defs>
            {SERIES.map(({ key, color }) => (
              <linearGradient key={key} id={`grad-${key}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={color} stopOpacity={0.25} />
                <stop offset="95%" stopColor={color} stopOpacity={0} />
              </linearGradient>
            ))}
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
          <XAxis
            dataKey="date"
            stroke="rgba(255,255,255,0.25)"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            stroke="rgba(255,255,255,0.25)"
            tick={{ fill: '#94a3b8', fontSize: 11 }}
            width={32}
            allowDecimals={false}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ stroke: 'rgba(255,255,255,0.1)', strokeWidth: 1 }} />
          {SERIES.map(({ key, color }) => (
            <Area
              key={key}
              type="monotoneX"
              dataKey={key}
              name={SERIES.find(s => s.key === key).label}
              stroke={color}
              strokeWidth={hidden[key] ? 0 : 2}
              fill={`url(#grad-${key})`}
              fillOpacity={hidden[key] ? 0 : 1}
              dot={false}
              activeDot={hidden[key] ? false : { r: 5, strokeWidth: 2, stroke: '#1e293b', fill: color }}
              strokeOpacity={hidden[key] ? 0 : 1}
            />
          ))}
        </AreaChart>
      </ResponsiveContainer>

      <div className="chart-legend">
        {SERIES.map(({ key, label, color }) => (
          <button
            key={key}
            className={`chart-legend-item${hidden[key] ? ' dimmed' : ''}`}
            onClick={() => toggleSeries(key)}
          >
            <span className="chart-legend-swatch" style={{ background: color }} />
            <span className="chart-legend-label">{label}</span>
          </button>
        ))}
      </div>
    </div>
  );
};

export default CommitActivityChart;
